import type { Messages } from '@lingui/core';

/**
 * Phase 1 locales - bundled directly for offline access.
 */
export const BUNDLED_LOCALES = ['en', 'fr', 'es'] as const;
export type BundledLocale = (typeof BUNDLED_LOCALES)[number];

/**
 * Static import map - bundlers can statically analyze these.
 * Using explicit imports instead of template strings for better tree-shaking.
 */
const catalogLoaders: Record<string, () => Promise<{ messages: Messages }>> = {
  en: () => import('../locales/en/messages'),
  fr: () => import('../locales/fr/messages'),
  es: () => import('../locales/es/messages'),
  // Future expansion (Phase 2+) - uncomment when adding UI translations:
  // de: () => import('../locales/de/messages'),
  // pt: () => import('../locales/pt/messages'),
  // it: () => import('../locales/it/messages'),
  // nl: () => import('../locales/nl/messages'),
  // ja: () => import('../locales/ja/messages'),
  // zh: () => import('../locales/zh/messages'),
};

/**
 * In-memory cache for loaded catalogs.
 */
const loadedCatalogs: Map<string, Messages> = new Map();

/**
 * Load a message catalog for the given locale.
 * Uses in-memory cache for instant subsequent loads.
 *
 * @param locale - The locale to load (e.g., 'en', 'fr', 'es')
 * @returns The loaded message catalog
 */
export async function loadCatalog(locale: string): Promise<Messages> {
  // Check in-memory cache first
  const cached = loadedCatalogs.get(locale);
  if (cached) return cached;

  const loader = catalogLoaders[locale];
  if (!loader) {
    // Guard against infinite recursion - if English is missing, we have a fatal error
    if (locale === 'en') {
      throw new Error('English locale is missing - cannot proceed');
    }
    console.warn(`Locale "${locale}" not found, falling back to English`);
    return loadCatalog('en');
  }

  const { messages } = await loader();
  loadedCatalogs.set(locale, messages);
  return messages;
}

/**
 * Preload all bundled locales.
 * Call on app init for instant language switching.
 */
export async function preloadBundledLocales(): Promise<void> {
  await Promise.all(BUNDLED_LOCALES.map(loadCatalog));
}

/**
 * Clear the catalog cache.
 * Useful for testing or hot reload scenarios.
 */
export function clearCatalogCache(): void {
  loadedCatalogs.clear();
}
